import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { twMerge } from "tailwind-merge";
import {
  ACTIVE_TENANT,
  TENANT_TEXT_CLASSES,
} from "../../../constants/tenant_colors";

interface PaginationV2Props {
  totalPage: number;
  /** Omit to let the component decide on its own: 3 on mobile (<=640px), 10 otherwise — matching the V1 convention every consumer used to replicate by hand. Pass a value only to override that default for a specific listing. */
  maxPageNumbersDisplayed?: number;
  totalRegister: number;
  actualPage: number;
  setNumberPage: any;
  className?: string;
}

export const PaginationV2 = ({
  actualPage,
  totalPage,
  maxPageNumbersDisplayed,
  setNumberPage,
  className = "pagination",
}: PaginationV2Props) => {
  const isMobile = useMediaQuery({ maxWidth: "640px" });
  const resolvedMaxPageNumbersDisplayed =
    maxPageNumbersDisplayed ?? (isMobile ? 3 : 10);
  const [selectedElement, setSelectedElement] = useState<number>(actualPage);
  const [qtdPageNumbersDisplayed, setQtdPageNumbers] = useState(
    totalPage < resolvedMaxPageNumbersDisplayed
      ? totalPage
      : resolvedMaxPageNumbersDisplayed,
  );
  const [pages, setPages] = useState<number[]>(
    new Array(qtdPageNumbersDisplayed).fill(0).map((n, index) => n + index),
  );

  useEffect(() => {
    if (selectedElement > pages[pages.length - 1]) {
      setPages(
        new Array(resolvedMaxPageNumbersDisplayed)
          .fill(selectedElement)
          .map((n, index) => selectedElement - index)
          .reverse(),
      );
      return;
    }

    if (selectedElement < pages[0]) {
      setPages(
        new Array(resolvedMaxPageNumbersDisplayed)
          .fill(0)
          .map((n, index) => n + index),
      );

      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedElement]);

  function first(number: number) {
    setNumberPage(number);
    setSelectedElement(number);
  }

  function next(number: number) {
    setNumberPage(number);
    setSelectedElement(number);
  }

  function prev(number: number) {
    setNumberPage(number);
    setSelectedElement(number);
  }

  function last(number: number) {
    setNumberPage(number);
    setSelectedElement(number);
  }

  return (
    <div
      className={twMerge(
        "flex gap-[1.6rem] mx-auto py-[0.8rem] items-center font-['Mulish'] w-max",
        className,
      )}
    >
      <div
        className={twMerge(
          "flex items-center [&_span]:cursor-pointer [&_span]:select-none [&_span]:text-base [&_span]:font-normal [&_span]:text-black/[.88] [&_span]:transition-colors [&_span:hover]:text-black/[.56] [&_span+span]:ml-5 max-[500px]:[&_span+span]:ml-0 max-[500px]:[#btn-first]:hidden",
          selectedElement === 0 && "opacity-40 pointer-events-none",
        )}
      >
        <span
          id="btn-first"
          onClick={() => first(0)}
          className="max-[500px]:hidden"
        >
          Primeira
        </span>
        <span
          onClick={() => prev(selectedElement <= 0 ? 0 : selectedElement - 1)}
        >
          Anterior
        </span>
      </div>

      <div className="flex gap-[0.8rem] flex-wrap">
        {pages.map((page, index) => (
          <button
            key={index}
            type="button"
            onClick={() => (setSelectedElement(page), setNumberPage(page))}
            className={twMerge(
              "px-[5px] py-[2px] border-none outline-none focus:outline-none font-semibold text-base bg-transparent text-[#20332f] transition-colors hover:bg-[#ebebeb]",
              page === selectedElement &&
                twMerge(
                  "w-max text-lg font-extrabold hover:bg-transparent",
                  TENANT_TEXT_CLASSES[ACTIVE_TENANT],
                ),
            )}
          >
            {page + 1}
          </button>
        ))}
      </div>

      <div
        className={twMerge(
          "flex items-center [&_span]:cursor-pointer [&_span]:select-none [&_span]:text-base [&_span]:font-normal [&_span]:text-black/[.88] [&_span]:transition-colors [&_span:hover]:text-black/[.56] [&_span+span]:ml-5 max-[500px]:[&_span+span]:ml-0",
          selectedElement === totalPage - 1 && "opacity-40 pointer-events-none",
        )}
      >
        <span
          onClick={() =>
            next(
              selectedElement >= totalPage - 1
                ? totalPage - 1
                : selectedElement + 1,
            )
          }
        >
          Próxima
        </span>
        <span
          id="btn-end"
          onClick={() => last(totalPage - 1)}
          className="max-[500px]:hidden"
        >
          Última
        </span>
      </div>
    </div>
  );
};
